/**
 * How a new release reaches a browser that already has the app.
 *
 * The service worker serves the app shell from its own copy, so a deploy
 * changes nothing on screen until the browser fetches the new worker — which
 * it does on a full page load, and a back-office tab is opened once in the
 * morning and never reloaded. Before this, a release reached such a tab the
 * next day, and the first load after a deploy still painted the old build.
 *
 * Now the browser is asked to look for a new worker when the tab comes back
 * into view and every hour. The worker takes over as soon as it is installed
 * (`autoUpdate` in vite.config.ts: skipWaiting + clientsClaim), and this page
 * then reloads into the new build at a safe moment:
 *   - straight away when nothing has been typed since this screen opened —
 *     which covers the first load after a deploy — and
 *   - otherwise on the next move to another screen, which is where unsaved
 *     edits are already guarded (useBlocker) and a reload costs nothing.
 *
 * The plugin's own registration (`virtual:pwa-register`) reloads the moment
 * the worker changes, under whatever the user is typing; its "prompt" mode
 * leaves the new worker waiting, which a tab opened by the previous build
 * never releases. Hence registering here.
 */
const CHECK_EVERY_MS = 60 * 60 * 1000;

let updateTaken = false;
let typedOnThisScreen = false;

function reloadIntoNewBuild(): void {
  window.location.reload();
}

export function startServiceWorker(): void {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;

  // Captured, so a field typed into keeps its screen from being reloaded.
  // `change` as well: a dropdown picked or a box ticked is an edit too, and
  // neither fires `input`.
  const markTyped = () => {
    typedOnThisScreen = true;
  };
  document.addEventListener('input', markTyped, true);
  document.addEventListener('change', markTyped, true);

  // A page with no worker yet is being claimed for the first time, not
  // updated: nothing to reload into.
  const hadWorker = Boolean(navigator.serviceWorker.controller);
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadWorker || updateTaken) return;
    updateTaken = true;
    if (!typedOnThisScreen) reloadIntoNewBuild();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        const check = () => {
          if (document.visibilityState !== 'visible' || !navigator.onLine) return;
          registration.update().catch(() => undefined);
        };
        setInterval(check, CHECK_EVERY_MS);
        document.addEventListener('visibilitychange', check);
      })
      .catch(() => {
        // No worker means no offline shell, not a broken app.
      });
  });
}

/** Called on every move to another screen: a release that arrived is taken here. */
export function onScreenChange(): void {
  typedOnThisScreen = false;
  if (updateTaken) reloadIntoNewBuild();
}
